import { FileUploadStatus, FileWithStatus } from '@/types/upload';
import { ERROR_MESSAGES, isInvalidFileTypeError } from '@/utils/validation';
import { useSupportedFileTypes } from '@/hooks/useSupportedFileTypes';
import {
  getFileStatusText,
  isDuplicateUploadFailure,
  isErrorUploadFailure,
  isWarningUploadFailure,
} from '@/utils/getFileStatusText';
import { CheckCircle, ChevronRight, Error, Pending, Warning } from '@mui/icons-material';
import { Button, Tooltip, styled } from '@mui/material';
import { ReactElement, ReactNode } from 'react';

const ErrorContainer = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const SecondaryText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

/**
 * The "Invalid File Type" tooltip. A component rather than a constant so the listed extensions
 * follow the tenant's USD gate — three call sites would otherwise each need the flag.
 */
export const SupportedFileTypesTooltip = ({ children }: { children: ReactElement }) => {
  const { supportedFileTypesMessage } = useSupportedFileTypes();
  return <Tooltip title={supportedFileTypesMessage}>{children}</Tooltip>;
};

export const FileStatusIcon = ({
  file,
  size = 'small',
}: {
  file: FileWithStatus;
  size?: 'small' | 'medium' | 'large';
}) => {
  const fontSize = size;

  if (file.status.state === FileUploadStatus.COMPLETED) {
    return <CheckCircle color='success' fontSize={fontSize} />;
  }

  if (file.status.state === FileUploadStatus.FAILED) {
    if (isWarningUploadFailure(file)) {
      return <Warning color='warning' fontSize={fontSize} />;
    }

    if (isInvalidFileTypeError(file.status.message)) {
      return (
        <SupportedFileTypesTooltip>
          <Error color='error' fontSize={fontSize} style={{ cursor: 'text' }} />
        </SupportedFileTypesTooltip>
      );
    }

    return <Error color='error' fontSize={fontSize} />;
  }

  return <Pending color='disabled' fontSize={fontSize} />;
};

export const FileErrorMessage = ({ file }: { file: FileWithStatus }) => {
  if (isDuplicateUploadFailure(file)) {
    return <SecondaryText>{file.status.message ?? 'Already exists'}</SecondaryText>;
  }

  if (file.status.state === FileUploadStatus.FAILED) {
    return (
      <ErrorContainer>
        {isInvalidFileTypeError(file.status.message) ? (
          <SupportedFileTypesTooltip>
            <span
              style={{
                cursor: 'text',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.status.message}
            </span>
          </SupportedFileTypesTooltip>
        ) : (
          <span>{file.status.message ?? ERROR_MESSAGES.UPLOAD_FAILED}</span>
        )}
        {file.status.traceId && <span>{`Error code: ${file.status.traceId}`}</span>}
      </ErrorContainer>
    );
  }

  if (file.status.state === FileUploadStatus.COMPLETED) {
    return <SecondaryText>Uploaded</SecondaryText>;
  }

  return <SecondaryText>Queued</SecondaryText>;
};

export const FileStatusText = ({ file }: { file: FileWithStatus }) => {
  return (
    <SecondaryText sx={{ color: isErrorUploadFailure(file) ? 'error.main' : undefined }}>
      • {getFileStatusText(file)}
    </SecondaryText>
  );
};

export const UploadStatusBadge = ({
  count,
  variant,
  onClick,
  icon,
  testId,
  ariaLabel,
  tooltipText,
}: {
  count: number;
  variant: 'success' | 'error';
  onClick: () => void;
  icon: ReactNode;
  testId: string;
  ariaLabel: string;
  tooltipText: string;
}) => {
  return (
    <Tooltip title={tooltipText}>
      <Button
        variant='contained'
        size='small'
        color={variant}
        onClick={onClick}
        startIcon={icon}
        endIcon={<ChevronRight fontSize='small' />}
        data-testid={testId}
        aria-label={ariaLabel}
        sx={{
          textTransform: 'none',
          minWidth: 0,
          borderRadius: 2,
          px: 1,
          py: 0.25,
          gap: 0.5,
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {count}
      </Button>
    </Tooltip>
  );
};
